import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

export interface FavoriteLocation {
  id: string;
  name: string;
  address: string;
  coordinates: { lat: number; lng: number };
  type?: 'restaurant' | 'cafe' | 'office' | 'public' | 'other';
  createdAt: Date | string;
  usageCount?: number;
}

export interface OfficeLocation {
  name: string;
  address: string;
  coordinates?: { lat: number; lng: number };
}

/**
 * Get favorite locations for the current user
 */
export async function getFavoriteLocations(): Promise<FavoriteLocation[]> {
  if (!auth.currentUser) {
    return [];
  }

  try {
    const userProfileDoc = await getDoc(doc(db, 'userProfiles', auth.currentUser.uid));
    if (userProfileDoc.exists()) {
      const data = userProfileDoc.data();
      return (data.favoriteLocations || []) as FavoriteLocation[];
    }
    return [];
  } catch (error) {
    console.error('Error fetching favorite locations:', error);
    return [];
  }
}

/**
 * Get user's office location from their profile
 * Tries multiple fields: officeLocation, companyLocation, location, city+country
 */
export async function getUserOfficeLocation(userId?: string): Promise<OfficeLocation | null> {
  const targetUserId = userId || auth.currentUser?.uid;
  
  if (!targetUserId) {
    return null;
  }

  try {
    const userProfileDoc = await getDoc(doc(db, 'userProfiles', targetUserId));
    if (userProfileDoc.exists()) {
      const data = userProfileDoc.data();
      
      console.log('[LocationService] Profile fields for user:', targetUserId, {
        hasOfficeLocation: !!data.officeLocation,
        companyLocation: data.companyLocation || '(niet ingesteld)',
        location: data.location || '(niet ingesteld)',
        city: data.city || '(niet ingesteld)',
        postcode: data.postcode || '(niet ingesteld)',
        country: data.country || '(niet ingesteld)',
      });
      
      // 1. Structured officeLocation object (has coordinates)
      if (data.officeLocation) {
        console.log('[LocationService] Found officeLocation object');
        return data.officeLocation as OfficeLocation;
      }
      
      // 2. companyLocation string (vestigingslocatie)
      if (data.companyLocation && data.companyLocation.trim()) {
        console.log('[LocationService] Found companyLocation:', data.companyLocation);
        return {
          name: data.company || data.displayName || 'Kantoor',
          address: data.companyLocation.trim(),
        };
      }
      
      // 3. General location field
      if (data.location && data.location.trim()) {
        console.log('[LocationService] Found location:', data.location);
        return {
          name: data.company || data.displayName || 'Locatie',
          address: data.location.trim(),
        };
      }
      
      // 4. Compose from city + postcode + country
      const parts = [data.city, data.postcode, data.country].filter(Boolean);
      if (parts.length > 0) {
        const composedAddress = parts.join(', ');
        console.log('[LocationService] Composed address from fields:', composedAddress);
        return {
          name: data.company || data.displayName || 'Locatie',
          address: composedAddress,
        };
      }
      
      console.log('[LocationService] No location fields found for user:', targetUserId);
    } else {
      console.log('[LocationService] No profile found for user:', targetUserId);
    }
    return null;
  } catch (error) {
    console.error('Error fetching user office location:', error);
    return null;
  }
}
